import { Box, IconButton, Stack, useTheme } from '@mui/material';
import {
  useCallback,
  useRef,
  useMemo,
  DragEvent,
  ChangeEvent,
  KeyboardEvent,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { Button, Typography } from '@components/atoms';
import { Dialogue } from '@components/molecules/Dialogue';
import { FileUploaderProps } from './FileUploader.props';
import { getTraceId } from '@/utils/traceId';
import { getFileUploaderStyles } from './FileUploader.theme';

// Import validation constants from shared utils
const DEFAULT_ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.jpeg',
  '.jpg',
  '.png',
  '.tiff',
  '.tif',
  '.xlsx',
  '.csv',
];
const DEFAULT_MAX_FILES = 15;
const DEFAULT_MAX_FILE_SIZE = 52428800; // 50 MB in bytes

export const FileUploader = ({
  acceptedExtensions = DEFAULT_ACCEPTED_EXTENSIONS,
  maxFiles = DEFAULT_MAX_FILES,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  onFilesSelected,
  showAsModal = false,
  open = false,
  onClose,
  title = 'Add Documents to Loan',
  disabled = false,
  dialogueSize = 'md',
  children,
}: FileUploaderProps) => {
  const theme = useTheme();
  const styles = getFileUploaderStyles(theme);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxSizeMB = useMemo(() => maxFileSize / (1024 * 1024), [maxFileSize]);

  const processFiles = useCallback((fileList: FileList | File[]): File[] => {
    // Return ALL files without filtering
    // The parent component (DocumentUploadManager) will handle validation and display
    if (!fileList || (Array.isArray(fileList) && fileList.length === 0)) {
      return [];
    }
    return Array.from(fileList).filter((file) => file instanceof File);
  }, []);

  // Handle files
  const handleFiles = useCallback(
    (fileList: FileList | File[]) => {
      const allFiles = processFiles(fileList);
      if (allFiles.length > 0) {
        onFilesSelected?.(allFiles);
      }
    },
    [onFilesSelected, processFiles]
  );

  // Drag handlers
  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (disabled) return;

      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles.length > 0) {
        handleFiles(droppedFiles);
      }
    },
    [disabled, handleFiles]
  );

  // Click to upload
  const handleClick = useCallback(() => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  }, [disabled]);

  const handleFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      try {
        const selectedFiles = e.target.files;
        if (selectedFiles && selectedFiles.length > 0) {
          handleFiles(selectedFiles);
        }
      } catch (error) {
        const log = {
          t: new Date().toISOString(),
          severity: 'ERROR',
          message: 'Error processing selected files',
          error: error instanceof Error ? error.message : String(error),
          stack_trace: error instanceof Error ? (error.stack ?? null) : null,
          trace_id: getTraceId(),
        };
        // Structured single-line JSON error using console.log (avoid console.error in production)
        console.log(JSON.stringify(log));
      } finally {
        // Reset input value to allow selecting the same file again
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [handleFiles]
  );

  // Handle close
  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick]
  );

  const uploaderContent = (
    <Stack sx={styles.container}>
      {/* Header with close button - only show when in modal */}
      {showAsModal && (
        <Box sx={styles.header}>
          <Box sx={styles.closeButtonWrapper}>
            <IconButton
              onClick={handleClose}
              sx={styles.closeButton}
              aria-label="Close dialog"
            >
              <CloseIcon sx={styles.closeIcon} />
            </IconButton>
          </Box>
          <Box>
            <Typography sx={styles.titleText}>{title}</Typography>
          </Box>
        </Box>
      )}

      {/* Dropzone */}
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-label="File upload dropzone. Click or press Enter to select files, or drag and drop files here"
        aria-disabled={disabled}
        sx={{
          ...styles.dropzone,
          ...(disabled ? styles.dropzoneDisabled : {}),
        }}
      >
        <Stack spacing={1} alignItems="center" gap={1}>
          <FileUploadOutlinedIcon sx={styles.uploadIcon} />
          <Typography sx={styles.instructionText}>
            <Box component="span" sx={styles.clickText}>
              Click
            </Box>
            {` to upload or drag and drop file here. Max files limit: ${maxFiles}, max file size: ${maxSizeMB} MB.`}
          </Typography>
          <Typography sx={styles.supportedFormatsText}>
            <Box component="span" sx={styles.supportedFormatsLabel}>
              Supported formats:
            </Box>
            {` ${acceptedExtensions.join(', ')}`}
          </Typography>
        </Stack>
      </Box>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedExtensions.join(',')}
        onChange={handleFileInputChange}
        disabled={disabled}
        aria-label="File upload input"
        style={styles.fileInput}
      />

      {/* Render children (e.g., FileUploadStatusWrapper components) */}
      {children && <Box sx={{ mt: 2 }}>{children}</Box>}

      {showAsModal && (
        <Box sx={styles.buttonContainer}>
          <Button variant="text" onClick={handleClose}>
            Close
          </Button>
          <Button variant="filled" onClick={handleClose}>
            Done
          </Button>
        </Box>
      )}
    </Stack>
  );

  // Render as modal or standalone
  if (showAsModal) {
    return (
      <Dialogue open={open} onClose={handleClose} size={dialogueSize ?? 'md'}>
        {uploaderContent}
      </Dialogue>
    );
  }

  return uploaderContent;
};

export default FileUploader;
